<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import ContactsCard from 'dashboard/components-next/Contacts/ContactsCard/ContactsCard.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits(['toggleContact', 'update:sort']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const expandedCardId = ref(null);
const hoveredAvatarId = ref(null);

// MedUp overlay: card <-> table view toggle (persisted). Patients need a flat
// scannable grid (who has a phone, who doesn't); cards-only is hard to skim.
const VIEW_KEY = 'cw_contacts_view_mode';
const viewMode = ref(
  localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'card'
);
const setView = mode => {
  viewMode.value = mode;
  localStorage.setItem(VIEW_KEY, mode);
};

// Server-side column sorting: emits up to the parent's handleSort (sorts the
// whole dataset, not just loaded rows). First click on a column = ascending,
// click again = descending. `phone_number` isn't in Chatwoot's sort menu but
// the contacts API accepts it.
const toggleSort = field => {
  const order =
    props.activeSort === field && props.activeOrdering === '' ? '-' : '';
  emit('update:sort', { sort: field, order });
};
const sortIndicator = field => {
  if (props.activeSort !== field) return '';
  return props.activeOrdering === '-' ? '↓' : '↑';
};

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));

const updateContact = async updatedData => {
  try {
    await store.dispatch('contacts/update', updatedData);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};

const isSelected = id => selectedIdsSet.value.has(id);

const shouldShowSelection = id => {
  return hoveredAvatarId.value === id || isSelected(id);
};

const handleSelect = (id, value) => {
  emit('toggleContact', { id, value });
};

const handleAvatarHover = (id, isHovered) => {
  hoveredAvatarId.value = isHovered ? id : null;
};

// MedUp's created/updated timestamps (ISO8601 in additional_attributes, deep-camelized
// to medupCreatedAt/medupUpdatedAt on the contact object). Render as dd.mm.yyyy hh:mm.
const fmtDate = value => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Date of birth "YYYY-MM-DD" → "DD.MM.YYYY". Parse by string (no Date()) to avoid
// timezone shifting a date-only value to the previous day.
const fmtDob = value => {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value || '';
};

// MedUp: contact channel bindings (payload channel_bindings). One badge per channel, coloured
// by channel, prefixed by the branch(es) the bot serves (phone channels = "общий").
const CH = {
  telegram: { icon: 'i-ph-telegram-logo', cls: 'bg-n-blue-3 text-n-blue-11' },
  whatsapp: { icon: 'i-ph-whatsapp-logo', cls: 'bg-n-teal-3 text-n-teal-11' },
  max: { icon: 'i-ph-chat-circle-dots', cls: 'bg-n-amber-3 text-n-amber-11' },
  sms: { icon: 'i-ph-chat-text', cls: 'bg-n-slate-3 text-n-slate-11' },
  other: { icon: 'i-ph-plugs-connected', cls: 'bg-n-slate-3 text-n-slate-11' },
};
const chMeta = channel => CH[channel] || CH.other;

// Flatten bindings into rows: one per (binding × serving branch); phone channels with no branch
// collapse to a single "общий" row.
const bindingRows = contact => {
  const rows = [];
  (contact.channelBindings || []).forEach(b => {
    const branches =
      b.branches && b.branches.length ? b.branches.map(x => x.name) : ['общий'];
    branches.forEach(branch =>
      rows.push({
        branch,
        channel: b.channel,
        bot: b.bot,
        sourceId: b.sourceId,
        unsubscribed: b.unsubscribed,
      })
    );
  });
  return rows;
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- view toggle -->
    <div class="flex items-center justify-end gap-1">
      <button
        type="button"
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak transition-colors"
        :class="
          viewMode === 'card'
            ? 'bg-n-slate-3 text-n-slate-12 dark:bg-n-solid-3'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        title="Карточки"
        @click="setView('card')"
      >
        <span class="i-ph-squares-four size-4" />
        Карточки
      </button>
      <button
        type="button"
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak transition-colors"
        :class="
          viewMode === 'table'
            ? 'bg-n-slate-3 text-n-slate-12 dark:bg-n-solid-3'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        title="Таблица"
        @click="setView('table')"
      >
        <span class="i-ph-table size-4" />
        Таблица
      </button>
    </div>

    <!-- card view (original) -->
    <template v-if="viewMode === 'card'">
      <div v-for="contact in contacts" :key="contact.id" class="relative">
        <ContactsCard
          :id="contact.id"
          :name="contact.name"
          :email="contact.email"
          :company-id="contact.companyId"
          :thumbnail="contact.thumbnail"
          :phone-number="contact.phoneNumber"
          :additional-attributes="contact.additionalAttributes"
          :availability-status="contact.availabilityStatus"
          :is-expanded="expandedCardId === contact.id"
          :is-updating="isUpdating"
          :selectable="shouldShowSelection(contact.id)"
          :is-selected="isSelected(contact.id)"
          @toggle="toggleExpanded(contact.id)"
          @update-contact="updateContact"
          @show-contact="onClickViewDetails"
          @select="value => handleSelect(contact.id, value)"
          @avatar-hover="value => handleAvatarHover(contact.id, value)"
        />
      </div>
    </template>

    <!-- flat table view -->
    <div
      v-else
      class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1"
    >
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="w-10 px-3 py-2.5" />
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('name')"
              >
                ФИО
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('name')
                }}</span>
              </button>
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('medup_sex')"
              >
                Пол
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('medup_sex')
                }}</span>
              </button>
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('medup_dob')"
              >
                Дата рожд.
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('medup_dob')
                }}</span>
              </button>
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('medup_cardnumber')"
              >
                ЭМК
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('medup_cardnumber')
                }}</span>
              </button>
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('crm_id')"
              >
                CRM ID
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('crm_id')
                }}</span>
              </button>
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('phone_number')"
              >
                Телефон
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('phone_number')
                }}</span>
              </button>
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              Каналы
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('email')"
              >
                Email
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('email')
                }}</span>
              </button>
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('medup_created_at')"
              >
                Создан
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('medup_created_at')
                }}</span>
              </button>
            </th>
            <th
              class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 hover:text-n-slate-12"
                @click="toggleSort('medup_updated_at')"
              >
                Обновлён
                <span class="w-2 text-n-slate-12">{{
                  sortIndicator('medup_updated_at')
                }}</span>
              </button>
            </th>
            <th class="w-10 px-3 py-2.5" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="contact in contacts"
            :key="contact.id"
            class="border-b cursor-pointer border-n-weak last:border-0 hover:bg-n-alpha-1"
            @click="onClickViewDetails(contact.id)"
          >
            <td class="px-3 py-2.5" @click.stop>
              <Checkbox
                :model-value="isSelected(contact.id)"
                @update:model-value="value => handleSelect(contact.id, value)"
              />
            </td>
            <td
              class="px-3 py-2.5 font-medium text-n-slate-12 whitespace-nowrap"
            >
              {{ contact.name }}
            </td>
            <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">
              <span v-if="contact.additionalAttributes?.medupSexName">{{
                contact.additionalAttributes.medupSexName
              }}</span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap tabular-nums">
              <span v-if="contact.additionalAttributes?.medupDob">{{
                fmtDob(contact.additionalAttributes.medupDob)
              }}</span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap tabular-nums">
              <span v-if="contact.additionalAttributes?.cardnumber">{{
                contact.additionalAttributes.cardnumber
              }}</span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap tabular-nums">
              <span
                v-if="contact.identifier && !contact.identifier.startsWith('mres3:')"
              >{{ contact.identifier }}</span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">
              <span v-if="contact.phoneNumber" class="tabular-nums">{{
                contact.phoneNumber
              }}</span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5" @click.stop>
              <div
                v-if="bindingRows(contact).length"
                class="flex flex-col gap-1"
              >
                <div
                  v-for="(r, i) in bindingRows(contact)"
                  :key="i"
                  class="flex items-center gap-1.5 flex-wrap"
                >
                  <span
                    class="inline-flex items-center gap-1 text-[11px] text-n-slate-10 min-w-[56px]"
                  >
                    <span class="i-ph-buildings size-3 shrink-0" />{{ r.branch }}
                  </span>
                  <span
                    :class="chMeta(r.channel).cls"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs whitespace-nowrap"
                    :title="r.sourceId || ''"
                  >
                    <span :class="chMeta(r.channel).icon" class="size-3.5" />
                    <span :class="{ 'line-through opacity-60': r.unsubscribed }">{{
                      r.bot
                    }}</span>
                  </span>
                  <span v-if="r.unsubscribed" class="text-[11px] text-n-ruby-11"
                    >отписан</span
                  >
                </div>
              </div>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 text-n-slate-11">
              <span v-if="contact.email" class="truncate">{{
                contact.email
              }}</span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 whitespace-nowrap text-n-slate-11 tabular-nums">
              <span v-if="contact.additionalAttributes?.medupCreatedAt">{{
                fmtDate(contact.additionalAttributes.medupCreatedAt)
              }}</span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 whitespace-nowrap text-n-slate-11 tabular-nums">
              <span v-if="contact.additionalAttributes?.medupUpdatedAt">{{
                fmtDate(contact.additionalAttributes.medupUpdatedAt)
              }}</span>
              <span v-else class="text-n-slate-10">—</span>
            </td>
            <td class="px-3 py-2.5 text-right" @click.stop>
              <button
                type="button"
                class="p-1 rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                title="Подробнее"
                @click="onClickViewDetails(contact.id)"
              >
                <span class="i-ph-arrow-right size-4" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
